// Pure shape helpers for arbiter-owned player databases: how a player document
// is built, how it is searched, and how an applied-report event is identified.
//
// Split out of js/services/player-db.js purely so it can be unit-tested — that
// module imports the Firebase SDK from a gstatic URL, which Node cannot resolve.
// No DOM, no Firestore, no state.

/**
 * "Lastname, Givenname" — the same convention the FIDE rating list uses, so a
 * player from either source sorts and displays identically.
 */
export function formatDbPlayerName(lastName, givenName) {
    const last = (lastName || '').trim();
    const given = (givenName || '').trim();
    if (last && given) return `${last}, ${given}`;
    return last || given;
}

/**
 * The lowercased haystack every search token is matched against. Club, federation
 * and the id fields are folded in so an arbiter can search for everyone from one
 * club, or paste a FIDE id straight into the box.
 */
export function buildSearchKey(p) {
    const player = p || {};
    return [player.name, player.club, player.fed, player.fideId, player.nsfId, player.memberNo]
        .filter(Boolean).join(' ').toLowerCase();
}

/**
 * Fill in the derived fields and drop anything outside the player schema, so a
 * document written by an import, the add-player form or a tournament roster all
 * end up identically shaped.
 *
 * Deliberately does NOT carry `ratings` through: ratings move only through the
 * Cloud Function (the security rules pin the map on update), so a normalized
 * patch is always safe to send. createPlayer adds the opening ratings itself.
 */
export function normalizePlayer(input) {
    const p = input || {};
    const lastName = (p.lastName || '').trim();
    const givenName = (p.givenName || '').trim();
    const out = {
        lastName,
        givenName,
        name: formatDbPlayerName(lastName, givenName),
        title: (p.title || '').trim(),
        club: (p.club || '').trim(),
        fed: (p.fed || '').trim().toUpperCase(),
        sex: (p.sex || '').trim(),
        birthYear: p.birthYear ? String(p.birthYear).trim() : '',
        birthDate: p.birthDate ? String(p.birthDate).trim() : '',
        fideId: p.fideId ? String(p.fideId).trim() : '',
        nsfId: p.nsfId ? String(p.nsfId).trim() : '',
        memberNo: p.memberNo ? String(p.memberNo).trim() : '',
        active: p.active !== false
    };
    out.searchKey = buildSearchKey(out);
    return out;
}

/**
 * Search a list of player records.
 *
 * Matching semantics are deliberately identical to the FIDE search in
 * functions/lib/fide.js — lowercase the query, split on whitespace, and require
 * EVERY token to appear somewhere in the player's search key — so the two sources
 * in the Add Player dialog behave the same way.
 */
export function searchPlayers(players, queryText, max = 15) {
    const tokens = String(queryText || '').toLowerCase().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return [];

    const results = [];
    for (const p of (Array.isArray(players) ? players : [])) {
        if (!p) continue;
        const key = p.searchKey || buildSearchKey(p);
        let match = true;
        for (const token of tokens) {
            if (!key.includes(token)) { match = false; break; }
        }
        if (!match) continue;
        results.push(p);
        if (results.length >= max) break;
    }
    return results;
}

/**
 * The deterministic id of the event produced by reporting one tournament group to
 * one rating list.
 *
 * Determinism is the whole point: reporting the same group twice targets the same
 * document, so a double-apply is impossible at the document level rather than by
 * convention. Every character outside [A-Za-z0-9_-] is replaced so the result is
 * always a legal Firestore document id.
 */
export function eventIdFor(tournamentId, groupId, listId) {
    return [tournamentId, groupId || 'main', listId]
        .map(s => String(s === undefined || s === null ? '' : s).replace(/[^\w-]/g, '_'))
        .join('__');
}

/** The config for one rating list within a database's `lists` array. */
export function findRatingList(database, listId) {
    if (!database || !Array.isArray(database.lists)) return null;
    return database.lists.find(l => l && l.id === listId) || null;
}
